import { type Action, type Subject, holdsAnywhere } from '@carii/casl-abilities';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useAbility } from '../../context/AbilityContext.js';

/**
 * 063 (gap-list H11): what a destination requires of its caller.
 *
 * `'always'` is a deliberate declaration, not the absence of one. Until 063 the
 * gate was an OPTIONAL `subject`, so an item that declared nothing was shown to
 * everyone `PrivateRoute` admits — which is how a provider came to be offered
 * `/users`, `/suppliers` and `/gift-certificates`, all of which the API refuses.
 *
 * The pair carries an ACTION as well as a subject because the old check asked
 * `can('manage', subject)`, and a provider holds `manage` on nothing: their
 * grants are per-action and scoped (`read PricingTemplate {supplier_id}`).
 * Gating on `manage` would have hidden the destinations they actually own.
 */
/**
 * `anywhere` (351): offered when the caller holds ANY of these at platform scope (`holdsAnywhere`), for screens that
 * read the whole platform. A type-level `can` would also admit a supplier's own-organisation grant.
 * `allAnywhere` (375): offered only when the caller holds EVERY one of them at platform scope, for a screen whose route
 * needs them all (the promo dashboard reads both gift certificates and discounts).
 */
export type Need =
  | readonly [Action, Subject]
  | 'always'
  | { readonly anywhere: ReadonlyArray<readonly [Action, Subject]> }
  | { readonly allAnywhere: ReadonlyArray<readonly [Action, Subject]> };

/** A link. `need` is required — an omission must not compile (FR-002). */
export interface NavLeaf {
  label: string;
  to: string;
  need: Need;
  icon?: ReactNode;
  badge?: string | number;
  /** 397: current on this exact path only (a sibling link lives under it, e.g. /penalties and /penalties/dashboard). */
  end?: boolean;
}

/** A heading. Visibility is DERIVED from its visible children, never declared. */
export interface NavGroup {
  label: string;
  children: NavItem[];
  icon?: ReactNode;
}

export type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup => 'children' in item;

interface SidebarNavProps {
  items: NavItem[];
  title?: string;
}

const leafClass = (isActive: boolean) =>
  [
    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
    isActive ? 'bg-primary/10 text-primary' : 'text-slate hover:bg-surface hover:text-ink',
  ].join(' ');

function Leaf({ item }: { item: NavLeaf }) {
  return (
    <NavLink to={item.to} end={item.end} className={({ isActive }) => leafClass(isActive)}>
      {item.icon && <span className="shrink-0">{item.icon}</span>}
      <span className="flex-1">{item.label}</span>
      {item.badge !== undefined && (
        <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

/** Collapsible parent grouping its (CASL-filtered) children. Default-open when
 * the current route matches one of the group's child links. */
function Group({
  item,
  visibleChildren,
  renderChild,
}: {
  item: NavGroup;
  visibleChildren: NavItem[];
  renderChild: (child: NavItem) => ReactNode;
}) {
  const { pathname } = useLocation();
  const activeInside = visibleChildren.some((c) => !isGroup(c) && pathname.startsWith(c.to));
  const [open, setOpen] = useState(activeInside);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-xs font-semibold text-steel uppercase tracking-wider hover:bg-surface"
      >
        {item.icon && <span className="shrink-0">{item.icon}</span>}
        <span className="flex-1 text-left">{item.label}</span>
        <span className={['shrink-0 transition-transform', open ? 'rotate-90' : ''].join(' ')}>
          <ChevronRight className="h-4 w-4" />
        </span>
      </button>
      {open && (
        <div className="ml-2 flex flex-col gap-0.5 border-l border-hairline-soft pl-2">
          {visibleChildren.map(renderChild)}
        </div>
      )}
    </div>
  );
}

export function SidebarNav({ items, title }: SidebarNavProps) {
  const ability = useAbility();

  // A leaf is offered only when the caller may perform the action it declares.
  // A group is offered only when something inside it is — derived, so a heading
  // can never outlive its contents (FR-006).
  const canSee = (item: NavItem): boolean =>
    isGroup(item)
      ? item.children.some(canSee)
      : item.need === 'always' ||
        ('anywhere' in item.need
          ? item.need.anywhere.some(([action, subject]) =>
              holdsAnywhere(ability as never, action, subject),
            )
          : 'allAnywhere' in item.need
            ? item.need.allAnywhere.every(([action, subject]) =>
                holdsAnywhere(ability as never, action, subject),
              )
            : ability.can(...item.need));

  const render = (item: NavItem): ReactNode =>
    isGroup(item) ? (
      <Group
        key={item.label}
        item={item}
        visibleChildren={item.children.filter(canSee)}
        renderChild={render}
      />
    ) : (
      <Leaf key={item.to} item={item} />
    );

  return (
    <nav className="flex flex-col gap-0.5" aria-label={title ?? 'Sidebar navigation'}>
      {title && (
        <p className="px-3 mb-1 text-xs font-semibold text-steel uppercase tracking-wider">
          {title}
        </p>
      )}
      {items.filter(canSee).map(render)}
    </nav>
  );
}
